// src/Components/HPC_Report/services/foundationalReportService.js
// Service for foundational stage (Grades 1-2) HPC reports
import { API_CONFIG } from '../../../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

/**
 * Fetches foundational stage HPC report data for the current student using background tasks
 * @returns {Promise<Object>} HPC report data
 */
export const fetchFoundationalReportData = async () => {
    try {
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            throw new Error("Authentication required. Please log in again.");
        }

        // Start background task for report generation with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        try {
            const generateResponse = await fetch(`${API_BASE_URL}/student/hpc-report/generate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!generateResponse.ok) {
                // If the generate endpoint fails, try the synchronous endpoint as fallback
                if (generateResponse.status === 504 || generateResponse.status === 502) {
                    console.warn("Background task endpoint timed out, falling back to synchronous endpoint");
                    return await fetchFoundationalReportDataSync();
                }
                const errorBody = await generateResponse.text();
                throw new Error(`HTTP error ${generateResponse.status}: ${generateResponse.statusText}. Details: ${errorBody}`);
            }

            const { task_id } = await generateResponse.json();

            // Poll for task completion
            return await pollForTaskCompletion(task_id, accessToken);

        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                console.warn("Request timed out, falling back to synchronous endpoint");
                return await fetchFoundationalReportDataSync();
            }
            throw error;
        }

    } catch (error) {
        console.error("Failed to fetch foundational report data:", error);
        // If all else fails, try the synchronous endpoint
        try {
            console.warn("Attempting synchronous fallback...");
            return await fetchFoundationalReportDataSync();
        } catch (fallbackError) {
            console.error("Synchronous fallback also failed:", fallbackError);
            throw error; // Throw the original error
        }
    }
};

/**
 * Fallback function to fetch report data synchronously (original method)
 * @returns {Promise<Object>} HPC report data
 */
const fetchFoundationalReportDataSync = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
    }

    const response = await fetch(`${API_BASE_URL}/student/hpc-report`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP error ${response.status}: ${response.statusText}. Details: ${errorBody}`);
    }

    return await response.json();
};

/**
 * Polls for task completion and returns the result
 * @param {string} taskId - The task ID to poll
 * @param {string} accessToken - The access token for authentication
 * @returns {Promise<Object>} The completed task result
 */
const pollForTaskCompletion = async (taskId, accessToken) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    const pollInterval = 5000; // 5 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            // Check task status
            const statusResponse = await fetch(`${API_BASE_URL}/tasks/${taskId}/status`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!statusResponse.ok) {
                throw new Error(`Failed to check task status: ${statusResponse.statusText}`);
            }

            const status = await statusResponse.json();

            if (status.status === 'completed') {
                // Get the result
                const resultResponse = await fetch(`${API_BASE_URL}/tasks/${taskId}/result`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!resultResponse.ok) {
                    throw new Error(`Failed to get task result: ${resultResponse.statusText}`);
                }

                return await resultResponse.json();
            } else if (status.status === 'failed') {
                throw new Error(`Report generation failed: ${status.error || 'Unknown error'}`);
            } else if (status.status === 'running' || status.status === 'pending') {
                // Log progress for debugging
                console.log(`Report generation progress: ${status.progress}% (${status.status})`);
                // Continue polling
                await new Promise(resolve => setTimeout(resolve, pollInterval));
                continue;
            }
        } catch (error) {
            console.warn(`Polling attempt ${attempt + 1}/${maxAttempts} failed:`, error.message);
            if (attempt === maxAttempts - 1) {
                throw new Error(`Report generation failed after ${maxAttempts} attempts: ${error.message}`);
            }
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
    }

    throw new Error('Report generation timed out. Please try again.');
};

/**
 * Fetches student's profile picture with CORS handling
 * @returns {Promise<string|null>} Base64 encoded image URL or null
 */
export const fetchProfilePicture = async () => {
    try {
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
            console.warn("No access token available for profile picture fetch");
            return null;
        }

        const server_url = API_BASE_URL;
        const response = await fetch(`${server_url}/users/me/profile-picture`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null; // No profile picture set
            }
            const errorData = await response.json();
            throw new Error(errorData.detail || "Failed to get profile picture");
        }

        const result = await response.json();

        if (result?.profile_picture_url) {
            const imgUrl = server_url + result.profile_picture_url;

            // Create a promise to handle image loading properly
            const imageLoaded = new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";

                img.onload = () => {
                    try {
                        // Create canvas and draw image
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);

                        // Convert to base64 - critical for PDF export
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
                        resolve(dataUrl);
                    } catch (err) {
                        console.error("Error converting image to base64:", err);
                        reject(err);
                    }
                };

                img.onerror = (err) => {
                    console.error("Error loading student image:", err);
                    reject(new Error("Failed to load image"));
                };

                // Set source to trigger loading
                img.src = imgUrl;
            });

            try {
                // Wait for image to load and convert to base64
                return await imageLoaded;
            } catch (error) {
                console.error("Error preparing student image:", error);
                return getDefaultProfileImage();
            }
        }
        return getDefaultProfileImage();
    } catch (error) {
        console.error("Error getting profile picture:", error);
        return getDefaultProfileImage();
    }
};

/**
 * Helper function to safely format a date string for display
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or error message
 */
export const displayDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.warn("Invalid date string provided for display:", dateString);
            return "Invalid Date";
        }
        return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    } catch (e) {
        console.error("Error formatting date for display:", dateString, e);
        return "Error Date";
    }
};

/**
 * Returns a default profile image when the real image can't be loaded
 * @returns {string} Data URL of a placeholder image
 */
function getDefaultProfileImage() {
    // Create a placeholder image
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');

    // Background color
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, 100, 100);

    // Border
    ctx.strokeStyle = '#4B5563';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, 98, 98);

    // Text for placeholder
    ctx.fillStyle = '#6B7280';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Student', 50, 50);

    return canvas.toDataURL('image/png');
}